import { useState } from 'react';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import {
  Avatar,
  Badge,
  Button,
  ContentLabel,
  ContentValue,
  FlexContainer,
  ProgressBar,
  RichTextValue
} from 'components/common';
import { Icon } from 'components/common/icons';
import { ConversationAnalysisSentimentLabels } from '.';

import type { AchievementDiscussionAnalysisResult } from 'shared-types';
import type { BadgeCategory } from 'components/common/Badge';

interface Props {
  number: number;
  numberOfAnswers: number;
  question: string;
  analysis: AchievementDiscussionAnalysisResult['sentimentAnalysisPerQuestion'][0];
}

const ConversationAnalysisQuestionsItem = ({
  number,
  numberOfAnswers,
  question,
  analysis
}: Props) => {
  const { messages, theme } = useReduxState();

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const sentimentDataByValue: {
    [value: string]: { category: BadgeCategory; label: string };
  } = {
    mixed: {
      category: 'warning-light',
      label: messages.commonMixed
    },
    negative: {
      category: 'error-light',
      label: messages.commonNegative
    },
    neutral: {
      category: 'dark',
      label: messages.commonNeutral
    },
    positive: {
      category: 'success-light',
      label: messages.commonPositive
    }
  };

  const isDisabled = Boolean(
    'message' in analysis && analysis.message === 'too_few_answers_to_analyse'
  );

  return (
    <div className="question">
      <div className="overview">
        <div className="overview-info">
          <div className="title">
            <div className="title-number">
              <Avatar fontSize="1.4rem">{number}</Avatar>
            </div>
            <div className="title-text">
              <RichTextValue html={question} />
            </div>
          </div>

          {'sentiment' in analysis ? (
            <div className="sentiment">
              <div className="labels">
                <ConversationAnalysisSentimentLabels
                  negative={analysis.sentiment.negative}
                  neutral={analysis.sentiment.neutral}
                  positive={analysis.sentiment.positive}
                />
              </div>

              <div className="progress">
                <ProgressBar
                  progresses={[
                    {
                      color: theme.successColor,
                      progress: analysis.sentiment.positive * 0.01
                    },
                    {
                      color: 'rgba(0, 0, 0, 0.4)',
                      progress:
                        (analysis.sentiment.positive +
                          analysis.sentiment.neutral) *
                        0.01
                    },
                    {
                      color: theme.dangerColor,
                      progress:
                        (analysis.sentiment.positive +
                          analysis.sentiment.neutral +
                          analysis.sentiment.negative) *
                        0.01
                    }
                  ]}
                  progressStyle={{ borderRadius: 20, height: 6 }}
                />
              </div>
            </div>
          ) : null}

          {'message' in analysis &&
          analysis.message === 'too_few_answers_to_analyse' ? (
            <div className="too-few-questions">
              {messages.achievementConversationAnalysesTooFewAnswers}
            </div>
          ) : null}
        </div>

        <div className="overview-show-more">
          <Button
            category="lighter"
            disabled={isDisabled}
            icon={isExpanded ? 'chevron-up' : 'chevron-down'}
            isRounded={true}
            onClick={() => setIsExpanded(!isExpanded)}
            size="small"
            text={
              isExpanded ? messages.commonShowLess : messages.commonShowMore
            }
          />
        </div>
      </div>

      {'observations' in analysis ? (
        <div className="more" style={{ display: isExpanded ? 'flex' : 'none' }}>
          <div className="info">
            <ContentLabel horizontal={true} label={messages.tasksAnswers}>
              <ContentValue>
                {getMessageWithPlaceholders({
                  message: messages.commonNumberOfAnswers,
                  placeholders: { numberOfAnswers }
                })}
              </ContentValue>
            </ContentLabel>

            <ContentLabel horizontal={true} label={messages.commonSummary}>
              <ContentValue>{analysis.summary}</ContentValue>
            </ContentLabel>
          </div>

          <div className="observations">
            <h4 className="observations-heading">
              {messages.achievementConversationAnalysesObservations}
            </h4>

            {analysis.observations?.length ? (
              <div className="observations-items">
                {analysis.observations.map((observation, observationIndex) => (
                  <div className="observation" key={observationIndex}>
                    <div className="observation-theme">{observation.theme}</div>

                    <div className="observation-description">
                      {observation.description}
                    </div>

                    <div className="observation-data">
                      <div className="observation-frequency">
                        <ProgressBar
                          color={theme.chartMainColor}
                          label={
                            <FlexContainer
                              style={{
                                gap: 20,
                                justifyContent: 'space-between'
                              }}
                            >
                              <div>
                                {
                                  messages.achievementConversationAnalysesFrequency
                                }
                              </div>
                              <div>{`${observation.prevalence}%`}</div>
                            </FlexContainer>
                          }
                          labelStyle={{
                            color: theme.textColorLighter,
                            marginBottom: 3
                          }}
                          progress={observation.prevalence * 0.01}
                        />
                      </div>
                      <div className="observation-sentiment">
                        <Badge
                          category={
                            sentimentDataByValue[observation.sentiment].category
                          }
                          text={
                            sentimentDataByValue[observation.sentiment].label
                          }
                        />
                      </div>
                    </div>

                    {observation.actionableItems?.length ? (
                      <div className="observation-actions">
                        {observation.actionableItems.map(
                          (action, actionIndex) => (
                            <div
                              className="observation-action"
                              key={actionIndex}
                            >
                              <div className="observation-action-icon">
                                <Icon icon="circle-arrow-right" />
                              </div>

                              <div className="observation-action-text">
                                {action}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <style jsx>{`
        .question {
          border: 1px solid ${theme.lightGreyDark};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
        }
        .title {
          align-items: center;
          display: flex;
          gap: 10px;
        }
        .title-text {
          color: ${theme.textColorLight};
          font-size: 1.6rem;
          font-weight: 600;
        }
        .overview {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          justify-content: space-between;
        }
        .overview-info {
          flex-grow: 1;
          flex-shrink: 1;
        }
        .sentiment {
          margin-top: 15px;
          max-width: 600px;
        }
        .progress {
          margin-top: 5px;
        }
        .overview-show-more {
          flex-grow: 0;
          flex-shrink: 0;
        }
        .more {
          flex-direction: column;
          gap: 15px;
        }
        .info {
          margin-top: 25px;
        }
        .observations {
          margin-top: 15px;
        }
        .observations-heading {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 10px;
          padding: 0 5px;
        }
        .observations-items {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        .observation {
          border: 1px solid ${theme.lightGreyDark};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
        }
        .observation-theme {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          font-weight: 700;
        }
        .observation-description {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          margin-top: 5px;
        }
        .observation-data {
          display: flex;
          flex-wrap: wrap;
          gap: 15px 30px;
          justify-content: space-between;
          margin-top: 20px;
        }
        .observation-frequency {
          width: 200px;
        }
        .observation-actions {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 15px;
          padding: 15px;
        }
        .observation-action {
          align-items: flex-start;
          display: flex;
          gap: 10px;
        }
        .observation-action-icon {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
        }
        .observation-action-text {
          color: ${theme.textColor};
          font-size: 1.4rem;
        }
        .too-few-questions {
          color: ${theme.textColorLighter};
          font-size: 1.5rem;
          margin-top: 15px;
        }
      `}</style>
    </div>
  );
};

export default ConversationAnalysisQuestionsItem;
